import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DEFAULT_THEME } from "../config";
import {
  applyTheme, normalizePreference, readStoredTheme, resolveTheme,
  subscribeSystemTheme, systemPrefersDark, writeStoredTheme,
} from "../theme";

// Validated once at load: an invalid deployment value warns a single time
// instead of on every render.
const CONFIGURED_PREFERENCE = normalizePreference(DEFAULT_THEME);

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  // `chosen` is the user's explicit choice; null means "not chosen yet".
  const [chosen, setChosen] = useState(readStoredTheme);
  const [prefersDark, setPrefersDark] = useState(systemPrefersDark);
  const theme = resolveTheme(chosen, CONFIGURED_PREFERENCE, prefersDark);

  useEffect(() => subscribeSystemTheme(setPrefersDark), []);
  useEffect(() => applyTheme(theme), [theme]);

  const setTheme = useCallback((next) => {
    writeStoredTheme(next);
    setChosen(next);
  }, []);
  const toggleTheme = useCallback(
    () => setTheme(theme === "dark" ? "light" : "dark"),
    [theme, setTheme],
  );

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
